import { createWithEqualityFn } from 'zustand/traditional';
import { immer } from 'zustand/middleware/immer';
/* eslint-disable no-console -- Analytics queue can fail before external logging initializes; keep direct fallback diagnostics. */
// import { FullStoryEvents } from '@integrations/analytics/FullStory';
// import { GtagEvents } from '@integrations/analytics/google.gtag';

/* TYPES */
export type TrackEventPayload = {
  eventType: string;
  identifier?: string; // Optional identifier for user
  data: Record<string, any>; // Object representing key-value pairs
  analyticsType: string; // This will specify which analytics tool is being used
};

export type AnalyticsState = {
  analyticsInitialized: boolean;
  eventsQueue: Record<string, Array<TrackEventPayload>>; // Queue by analytics type

  actions: {
    initializeAnalytics: (analyticsType: string) => void;
    trackEvent: (payload: TrackEventPayload) => void;
    flushQueue: (analyticsType: string) => void;
  };
};

/* CONSTANTS */
export const ANALYTICS_FULLSTORY = 'FullStory';
export const ANALYTICS_GOOGLE = 'GoogleAnalytics';
export const ANALYTICS_SEGMENT = 'SegmentAnalytics';

/* ----------------------------------------------------------------------- */
/* ---------------------  ANALYTICS STORE SETUP  ----------------------- */
/* ----------------------------------------------------------------------- */

const useAnalyticsStore = createWithEqualityFn<AnalyticsState>()(
  immer(set => ({
    analyticsInitialized: false,
    eventsQueue: {}, // Initialize the queue as an empty object

    actions: {
      /* Not used */
      initializeAnalytics: analyticsType => {},

      /* Handle if its loaded in the caller. TrackEvent is a que */
      trackEvent: ({ eventType, identifier, data, analyticsType }: TrackEventPayload) => {
        try {
          set(state => {
            const currentQue = state.eventsQueue[analyticsType] || [];
            // Remove any existing events with the same identifier (keep only the latest)
            const filteredQue = currentQue.filter(event => event.identifier !== identifier);
            const newEvent = { eventType, identifier, data, analyticsType };

            // FullStory specific call
            if (analyticsType === ANALYTICS_FULLSTORY) {
              state.eventsQueue[ANALYTICS_FULLSTORY] = filteredQue.concat([newEvent]);
            } else if (analyticsType === ANALYTICS_GOOGLE) {
              // Google Analytics specific call
              state.eventsQueue[ANALYTICS_GOOGLE] = filteredQue.concat([newEvent]);
            } else if (analyticsType === ANALYTICS_SEGMENT) {
              // Segment Analytics specific call - always keep the latest event
              state.eventsQueue[ANALYTICS_SEGMENT] = filteredQue.concat([newEvent]);

              // If analytics is already ready, flush immediately
              // Check if analytics exists and has methods (is ready)
              const hasWindow = typeof window !== 'undefined';
              const hasAnalytics = hasWindow && (window as any).analytics;
              const analyticsIsObject = hasAnalytics && typeof (window as any).analytics === 'object' && (window as any).analytics !== null;
              const analyticsReady = analyticsIsObject && typeof (window as any).analytics.track === 'function';
              if (analyticsReady) {
                // Use setTimeout to ensure this happens after state update
                setTimeout(() => {
                  try {
                    const store = useAnalyticsStore.getState();
                    if (store.eventsQueue[ANALYTICS_SEGMENT]?.length) {
                      store.actions.flushQueue(ANALYTICS_SEGMENT);
                    }
                  } catch (e) {
                    console.error('Error in auto-flush:', e);
                  }
                }, 0);
              }
            }
          });
        } catch (e) {
          console.error('Error in trackEvent:', e);
        }
      },

      flushQueue: (analyticsType: string) => {
        set(state => {
          if (state.eventsQueue[analyticsType]?.length) {
            // Capture the queue items before clearing
            const queueItems = [...state.eventsQueue[analyticsType]];

            // Clear the queue immediately to prevent duplicate processing
            state.eventsQueue[analyticsType] = [];

            // Process queue items asynchronously to handle Promises
            queueItems.forEach(({ eventType, identifier, data }) => {
              if (analyticsType === ANALYTICS_FULLSTORY) {
                // const d = Array.isArray(data) ? data.at(0) : data;
                // FullStoryEvents({ eventType, identifier, data: d });
              } else if (analyticsType === ANALYTICS_GOOGLE) {
                // const d = Array.isArray(data) ? data.at(0) : data;
                // GtagEvents({eventType, identifier, data: d});
              } else if (analyticsType === ANALYTICS_SEGMENT) {
                // Only flush if analytics is ready and has the method
                const hasWindow = typeof window !== 'undefined';
                const hasAnalytics = hasWindow && (window as any).analytics;
                const hasMethod = hasAnalytics && typeof (window as any).analytics[eventType] === 'function';

                if (hasMethod) {
                  try {
                    // Segment's track method signature: track(event, [properties], [options], [callback])
                    // The data array from pipeline is [mergedTrait, integrations] filtered
                    // When analytics exists, pipeline calls: analytics.track(identifier, ...mergedData)
                    // So we need to spread the array to match that behavior
                    const dataToPass = Array.isArray(data) ? data : [data];

                    // Match the pipeline behavior exactly: analytics[eventType](identifier, ...mergedData)
                    // The pipeline spreads mergedData, so we do the same
                    // IMPORTANT: Convert Proxy objects to plain objects so Segment can read them
                    const plainDataToPass = dataToPass.map((item: any) => {
                      if (item && typeof item === 'object') {
                        // Convert Proxy or any object to plain object
                        return { ...item };
                      }
                      return item;
                    });

                    // Match the pipeline: analytics[eventType](identifier, ...mergedData)
                    // @ts-ignore
                    const analyticsMethod = (window as any).analytics[eventType];
                    // @ts-ignore
                    const result = analyticsMethod.call((window as any).analytics, identifier, ...plainDataToPass);

                    // Handle Promise if returned
                    if (result instanceof Promise) {
                      result.catch(error => {
                        console.error('Error flushing Segment event:', error);
                      });
                    }
                  } catch (e) {
                    console.error('Error flushing Segment event:', e);
                  }
                }
              }
              // Other analytics flush logic
            });
          }
        });
      },
    },
  })),
);

/* Export Selectors */
export const useAnalyticsActions = () => useAnalyticsStore(state => state.actions);
export const useAnalyticsInitialized = () => useAnalyticsStore(state => state.analyticsInitialized);
export const useEventsQueue = (analyticsType: string) => useAnalyticsStore(state => state.eventsQueue[analyticsType]);

export default useAnalyticsStore;
